import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { login, register } from '@/lib/api/auth';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DOCUMENT_TYPES } from '@/lib/documentTypes';
import { loginModeFromParams, paramsForLoginMode, type LoginMode } from '@/lib/loginMode';
import {
  emptySignupFields,
  passwordMatchState,
  validateSignup,
  type SignupErrors,
  type SignupFields,
} from '@/lib/signupForm';

/** Whitelist of intra-app redirect targets — defends against open-redirect.
 *
 * ROUND19 #1: post-login default landing es `/` (no `/my-purchases`).
 * `from === '/login'` (redirect circular) también vuelve a `/`.
 */
function safeRedirectTarget(from: string | null): string {
  if (!from) return '/';
  if (!from.startsWith('/')) return '/';
  if (from.startsWith('//')) return '/'; // protocol-relative attempt
  if (from === '/login') return '/';
  return from;
}

/**
 * Pantalla de ingreso y registro.
 *
 * **El registro se despliega en la MISMA pantalla**, sin navegar a otra ruta.
 * Se mantuvo así porque es como ya funcionaba el enlace "Crear cuenta":
 * conservar la ruta `/login` deja intactos los redirects que llegan con
 * `?from=`, que se perderían al saltar a otra página.
 *
 * **Cuál de los dos se muestra sale de `?modo=`** (ver `@/lib/loginMode`), no
 * de un estado interno. Es lo que permite que los botones de la barra lateral
 * cambien el formulario incluso estando ya acá: siendo la misma ruta, el
 * componente no se vuelve a montar, así que un estado inicializado al montar se
 * habría quedado congelado.
 *
 * En `sign_up` se piden **nombre, correo, teléfono, fecha de nacimiento y
 * documento** (tipo + número).
 *
 * ⚠️ La **contraseña** se pide además porque `POST /auth/register` la exige: sin
 * ella no hay cuenta que crear.
 *
 * ⚠️ El **documento son DOS campos** aunque el requerimiento lo nombre en
 * singular: un número suelto no identifica a nadie —una cédula y una cédula de
 * extranjería pueden compartir dígitos— y el tipo es lo que las distingue. El
 * backend también lo modela así.
 */
export function Login(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const next = safeRedirectTarget(searchParams.get('from'));
  // 🔴 El modo se DERIVA de la dirección, no se guarda en estado.
  //
  // Guardado en estado, el inicializador corría una sola vez, al montar. Como
  // ingreso y registro son la MISMA ruta, ir de uno al otro no vuelve a montar
  // el componente: la dirección cambiaba y el formulario se quedaba igual.
  // Derivándolo, cambiar la dirección **es** cambiar el modo.
  const mode = loginModeFromParams(searchParams);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signup, setSignup] = useState<SignupFields>(emptySignupFields);
  const [fieldErrors, setFieldErrors] = useState<SignupErrors>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const isSignUp = mode === 'sign_up';
  const otherMode: LoginMode = isSignUp ? 'sign_in' : 'sign_up';
  const matchState = passwordMatchState(signup.password, signup.passwordConfirm);

  function setField(field: keyof SignupFields, value: string): void {
    setSignup((prev) => ({ ...prev, [field]: value }));
    // El error del campo se limpia al corregirlo: dejarlo puesto mientras el fan
    // reescribe convierte una ayuda en un reproche.
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (isSignUp) {
      const errors = validateSignup(signup);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return; // no se llama al backend con datos malos
    }

    setPending(true);
    try {
      if (!isSignUp) {
        await login(email, password);
        // El listener de auth en `src/store/cart.ts` switchea la storage key
        // del user y rehidrata su carrito (R21 #7 + R20 P0 cross-user).
        navigate(next, { replace: true });
      } else {
        await register(signup.email.trim(), signup.password, {
          name: signup.name.trim(),
          phone: signup.phone.trim(),
          birthDate: signup.birthDate,
          documentType: signup.documentType,
          documentNumber: signup.documentNumber.trim(),
        });
        // El registro devuelve sesión viva → entramos directo.
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos procesar la solicitud.');
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="container-fc py-12">
      <div className="mx-auto max-w-md">
        <h1 className="font-display text-3xl">{isSignUp ? 'Crear cuenta' : 'Ingresar'}</h1>
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" noValidate={isSignUp}>
          {isSignUp && (
            <Input
              label="Nombre"
              type="text"
              autoComplete="name"
              value={signup.name}
              error={fieldErrors.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          )}

          <Input
            label="Correo"
            type="email"
            autoComplete="email"
            required={!isSignUp}
            value={isSignUp ? signup.email : email}
            error={isSignUp ? fieldErrors.email : undefined}
            onChange={(e) =>
              isSignUp ? setField('email', e.target.value) : setEmail(e.target.value)
            }
          />

          {isSignUp && (
            <>
              <Input
                label="Teléfono"
                type="tel"
                autoComplete="tel"
                value={signup.phone}
                error={fieldErrors.phone}
                onChange={(e) => setField('phone', e.target.value)}
              />
              <Input
                label="Fecha de nacimiento"
                type="date"
                autoComplete="bday"
                value={signup.birthDate}
                error={fieldErrors.birthDate}
                onChange={(e) => setField('birthDate', e.target.value)}
              />

              {/*
                Documento: son DOS campos, no uno. Un número suelto no
                identifica a nadie —una cédula y una cédula de extranjería
                pueden compartir dígitos—, y el tipo es lo que las distingue en
                la puerta del evento y en los soportes de la compra.

                El `<select>` va a mano porque no hay primitiva `Select` en la
                librería de UI. Las clases son las mismas del `Input`, tomadas
                del formulario anterior, para que se vea como el resto.
              */}
              <div>
                <label
                  htmlFor="signup-doctype"
                  className="mb-1.5 block text-sm font-medium text-fc-ink"
                >
                  Tipo de documento
                </label>
                <select
                  id="signup-doctype"
                  value={signup.documentType}
                  aria-invalid={fieldErrors.documentType ? true : undefined}
                  aria-describedby={fieldErrors.documentType ? 'signup-doctype-err' : undefined}
                  onChange={(e) => setField('documentType', e.target.value)}
                  className={`block w-full rounded-card border bg-fc-paper px-3 py-2 text-base text-fc-ink focus:outline-none focus:ring-2 ${
                    fieldErrors.documentType
                      ? 'border-fc-accent focus:border-fc-accent focus:ring-fc-accent/40'
                      : 'border-fc-line focus:border-fc-gold focus:ring-fc-gold/40'
                  }`}
                >
                  {/*
                    Sin opción preseleccionada: obligar a elegir evita que quien
                    tiene pasaporte se registre como cédula por no haber mirado
                    el selector.
                  */}
                  <option value="">Elegí una opción</option>
                  {DOCUMENT_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.documentType && (
                  <p id="signup-doctype-err" role="alert" className="mt-1.5 text-sm text-fc-accent">
                    {fieldErrors.documentType}
                  </p>
                )}
              </div>

              <Input
                label="Número de documento"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                hint="El mismo que vas a presentar en el ingreso al evento."
                value={signup.documentNumber}
                error={fieldErrors.documentNumber}
                onChange={(e) => setField('documentNumber', e.target.value)}
              />
            </>
          )}

          <Input
            label="Contraseña"
            type="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            required={!isSignUp}
            minLength={8}
            value={isSignUp ? signup.password : password}
            error={isSignUp ? fieldErrors.password : undefined}
            onChange={(e) =>
              isSignUp ? setField('password', e.target.value) : setPassword(e.target.value)
            }
          />

          {isSignUp && (
            <div>
              <Input
                label="Confirmar contraseña"
                type="password"
                autoComplete="new-password"
                value={signup.passwordConfirm}
                // El error de envío manda sobre el aviso en vivo: si ya se
                // intentó enviar, lo que importa es qué falta corregir.
                error={fieldErrors.passwordConfirm}
                onChange={(e) => setField('passwordConfirm', e.target.value)}
              />
              {/*
                Aviso EN VIVO de coincidencia. `role="status"` y no `alert`: es
                información de apoyo, y un lector de pantalla no debería
                interrumpir al fan en cada tecla que escribe.
              */}
              {!fieldErrors.passwordConfirm && matchState !== 'pendiente' && (
                <p
                  role="status"
                  className={`mt-1.5 text-sm ${
                    matchState === 'coinciden' ? 'text-emerald-600' : 'text-fc-accent'
                  }`}
                >
                  {matchState === 'coinciden'
                    ? '✓ Las contraseñas coinciden.'
                    : 'Las contraseñas no coinciden.'}
                </p>
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-fc-accent">
              {error}
            </p>
          )}
          {notice && (
            <p role="status" className="text-sm text-fc-ink">
              {notice}
            </p>
          )}
          <Button type="submit" disabled={pending}>
            {pending ? 'Procesando…' : isSignUp ? 'Crear cuenta' : 'Ingresar'}
          </Button>
        </form>

        {/* ROUND19 #5: link a recuperar contraseña (sólo en modo sign_in). */}
        {!isSignUp && (
          <p className="mt-3 text-sm text-fc-muted">
            <Link to="/forgot-password" className="underline hover:text-fc-ink">
              ¿Olvidaste tu contraseña?
            </Link>
          </p>
        )}

        <p className="mt-4 text-sm text-fc-muted">
          {isSignUp ? '¿Ya tenés cuenta?' : '¿Sin cuenta?'}{' '}
          <button
            type="button"
            className="underline hover:text-fc-ink"
            onClick={() => {
              // Alternar = cambiar la dirección. Así el enlace de acá y los
              // botones de la barra hacen exactamente lo mismo, y no hay dos
              // caminos que puedan discrepar.
              //
              // `replace` para que "atrás" salga de la pantalla en vez de
              // deshacer el cambio de modo: quien vuelve atrás quiere irse, no
              // ver el otro formulario.
              setSearchParams(paramsForLoginMode(searchParams, otherMode), { replace: true });
              setError(null);
              setNotice(null);
              setFieldErrors({});
            }}
          >
            {isSignUp ? 'Ingresar' : 'Crear cuenta'}
          </button>
        </p>
      </div>
    </section>
  );
}
